import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import SearchableDropdown from "../../components/SearchableDropdown";

const PluginForm: React.FC = () => {
 const { t } = useTranslation();
 const { pluginId } = useParams<{ pluginId: string }>();
 const navigate = useNavigate();
 const { isAuthenticated } = usePlatformAuth();
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [formData, setFormData] = useState({
  id: "",
  name: "",
  description: "",
  version: "1.0.0",
  price: "",
  yearlyPrice: "",
  billingCycle: "both" as "monthly" | "yearly" | "both",
  category: "core",
  isActive: true,
  dependencies: [""],
  routes: [""],
 });

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (!pluginId || pluginId === "new") {
   // Cannot create new plugins - they must be registered in code
   setError("Plugins can only be created in the codebase. Please edit an existing plugin.");
   navigate("/platform/plugins");
   return;
  }

  fetchPlugin();
 }, [pluginId, isAuthenticated, navigate]);

 const fetchPlugin = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await platformApiService.getPlugins();
   const plugins = response.data.plugins || [];
   const plugin = plugins.find((p: any) => p._id === pluginId || p.id === pluginId);
   if (plugin) {
    setFormData({
     id: plugin.id || "",
     name: plugin.name || "",
     description: plugin.description || "",
     version: plugin.version || "1.0.0",
     price: plugin.price?.toString() || "",
     yearlyPrice: plugin.yearlyPrice?.toString() || "",
     billingCycle: plugin.billingCycle || "both",
     category: plugin.category || "core",
     isActive: plugin.isActive !== undefined ? plugin.isActive : true,
     dependencies: plugin.dependencies && plugin.dependencies.length > 0 ? plugin.dependencies : [""],
     routes: plugin.routes && plugin.routes.length > 0 ? plugin.routes : [""],
    });
   }
  } catch (err: any) {
   console.error("Error fetching plugin:", err);
   setError(err.response?.data?.error || "Failed to fetch plugin");
  } finally {
   setLoading(false);
  }
 };

 const handleArrayChange = (field: "dependencies" | "routes", index: number, value: string) => {
  const newArray = [...formData[field]];
  newArray[index] = value;
  setFormData({ ...formData, [field]: newArray });
 };

 const addArrayItem = (field: "dependencies" | "routes") => {
  setFormData({ ...formData, [field]: [...formData[field], ""] });
 };

 const removeArrayItem = (field: "dependencies" | "routes", index: number) => {
  const newArray = formData[field].filter((_, i) => i !== index);
  setFormData({ ...formData, [field]: newArray.length > 0 ? newArray : [""] });
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setLoading(true);

  try {
   // Only submit editable fields (pricing, billing, category, status, routes)
   // Metadata (id, name, description, version, dependencies) comes from code
   const submitData: any = {
    id: formData.id, // Required for identification, but not editable
    price: parseFloat(formData.price),
    billingCycle: formData.billingCycle,
    category: formData.category,
    isActive: formData.isActive,
    routes: formData.routes.filter((r) => r.trim() !== ""),
   };

   if (formData.yearlyPrice) {
    submitData.yearlyPrice = parseFloat(formData.yearlyPrice);
   }

   await platformApiService.updatePlugin(pluginId!, submitData);
   navigate("/platform/plugins");
  } catch (err: any) {
   console.error("Error saving plugin:", err);
   setError(err.response?.data?.error || "Failed to save plugin");
  } finally {
   setLoading(false);
  }
 };

 if (loading && pluginId && pluginId !== "new") {
  return <div className="p-8">Loading plugin...</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">Edit Plugin</h1>
    <button
     onClick={() => navigate("/platform/plugins")}
     className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
    >
     Back to Plugins
    </button>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
     {error}
    </div>
   )}

   <div className="bg-white shadow p-6">
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 ">
     <p className="text-sm text-blue-800">
      <strong>Note:</strong> Plugin metadata (ID, name, description, version, dependencies) is managed in the codebase and cannot be edited here. You can only edit pricing, billing, category, and status.
     </p>
    </div>
    <form onSubmit={handleSubmit} className="space-y-6">
     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Plugin ID
      </label>
      <input
       type="text"
       value={formData.id}
       disabled
       className="w-full px-3 py-2 border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed"
      />
      <p className="mt-1 text-sm text-gray-500">
       Managed in codebase
      </p>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Name
      </label>
      <input
       type="text"
       value={formData.name}
       disabled
       className="w-full px-3 py-2 border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed"
      />
      <p className="mt-1 text-sm text-gray-500">
       Managed in codebase
      </p>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Description
      </label>
      <textarea
       value={formData.description}
       disabled
       className="w-full px-3 py-2 border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed"
       rows={3}
      />
      <p className="mt-1 text-sm text-gray-500">
       Managed in codebase
      </p>
     </div>

     <div className="grid grid-cols-2 gap-4">
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Version
       </label>
       <input
        type="text"
        value={formData.version}
        disabled
        className="w-full px-3 py-2 border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed"
       />
       <p className="mt-1 text-sm text-gray-500">
        Managed in codebase
       </p>
      </div>

      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Category <span className="text-red-500">*</span>
       </label>
       <SearchableDropdown
        fetchOptions={async (search: string) => {
         const categories = [
          { _id: "core", label: "Core" },
          { _id: "payment", label: "Payment" },
          { _id: "marketing", label: "Marketing" },
          { _id: "support", label: "Support" },
          { _id: "analytics", label: "Analytics" },
          { _id: "integration", label: "Integration" },
         ];
         return categories.filter((cat) =>
          cat.label.toLowerCase().includes(search.toLowerCase())
         );
        }}
        placeholder={t("common.placeholders.selectCategory")}
        value={formData.category}
        getDisplayLabel={(val: string) => {
         const categoryMap: Record<string, string> = {
          core: "Core",
          payment: "Payment",
          marketing: "Marketing",
          support: "Support",
          analytics: "Analytics",
          integration: "Integration",
         };
         return categoryMap[val] || val;
        }}
        onSelect={(option) => {
         setFormData({ ...formData, category: option._id });
        }}
       />
      </div>
     </div>

     <div className="grid grid-cols-2 gap-4">
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Monthly Price ($) <span className="text-red-500">*</span>
       </label>
       <input
        type="number"
        step="0.01"
        min="0"
        required
        value={formData.price}
        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       />
      </div>

      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Yearly Price ($) <span className="text-gray-500">(optional)</span>
       </label>
       <input
        type="number"
        step="0.01"
        min="0"
        value={formData.yearlyPrice}
        onChange={(e) => setFormData({ ...formData, yearlyPrice: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       />
      </div>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Billing Cycle <span className="text-red-500">*</span>
      </label>
      <select
       value={formData.billingCycle}
       onChange={(e) =>
        setFormData({ ...formData, billingCycle: e.target.value as any })
       }
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
       <option value="monthly">Monthly Only</option>
       <option value="yearly">Yearly Only</option>
       <option value="both">Both Monthly and Yearly</option>
      </select>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Dependencies (Plugin IDs)
      </label>
      <div className="px-3 py-2 border border-gray-300 bg-gray-100">
       {formData.dependencies && formData.dependencies.length > 0 && formData.dependencies[0] ? (
        <div className="flex flex-wrap gap-2">
         {formData.dependencies.filter((d) => d.trim() !== "").map((dep, index) => (
          <span
           key={index}
           className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-sm"
          >
           {dep}
          </span>
         ))}
        </div>
       ) : (
        <span className="text-gray-500 text-sm">No dependencies</span>
       )}
      </div>
      <p className="mt-1 text-sm text-gray-500">
       Managed in codebase
      </p>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Routes (Route paths this plugin provides)
      </label>
      {formData.routes.map((route, index) => (
       <div key={index} className="flex gap-2 mb-2">
        <input
         type="text"
         value={route}
         onChange={(e) => handleArrayChange("routes", index, e.target.value)}
         className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder="/admin/plugin-route"
        />
        <button
         type="button"
         onClick={() => removeArrayItem("routes", index)}
         className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200"
        >
         Remove
        </button>
       </div>
      ))}
      <button
       type="button"
       onClick={() => addArrayItem("routes")}
       className="mt-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
      >
       + Add Route
      </button>
     </div>

     <div>
      <label className="flex items-center gap-2">
       <input
        type="checkbox"
        checked={formData.isActive}
        onChange={(e) =>
         setFormData({ ...formData, isActive: e.target.checked })
        }
        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
       />
       <span className="text-sm font-medium text-gray-700">Active</span>
      </label>
     </div>

     <div className="flex gap-2">
      <button
       type="submit"
       disabled={loading}
       className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
       {loading ? "Saving..." : "Update Plugin"}
      </button>
      <button
       type="button"
       onClick={() => navigate("/platform/plugins")}
       className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
       Cancel
      </button>
     </div>
    </form>
   </div>
  </div>
 );
};

export default PluginForm;

